import React, { useState, useEffect } from 'react';
import { ssoService } from '../../../services/ssoService';
import { Activity, CheckCircle, XCircle, AlertTriangle, TrendingUp } from 'lucide-react';

const ConnectionStatus = ({ organizationId, providerStatus }) => {
  const [authLogs, setAuthLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (organizationId) {
      loadAuthLogs();
    }
  }, [organizationId]);

  const loadAuthLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await ssoService?.getAuthLogs(organizationId, 10);
      if (!error && data) {
        setAuthLogs(data);
      }
    } catch (error) {
      console.error('Error loading auth logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProviderIcon = (provider) => {
    const icons = {
      okta: '🔷',
      azure_ad: '🔵',
      google_workspace: '🔴'
    };
    return icons?.[provider] || '🔒';
  };

  const getProviderName = (provider) => {
    const names = {
      okta: 'Okta',
      azure_ad: 'Azure AD',
      google_workspace: 'Google Workspace'
    };
    return names?.[provider] || provider;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'testing':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'inactive':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Activity className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Connection Status Dashboard</h2>
            <p className="text-sm text-muted-foreground">
              Monitor SSO provider health and authentication activity
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Provider Status Cards */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Provider Health</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {providerStatus?.map(status => (
              <div
                key={status?.provider}
                className={`p-4 rounded-lg border ${
                  getStatusColor(status?.connectionStatus)
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getProviderIcon(status?.provider)}</span>
                    <span className="font-semibold">
                      {getProviderName(status?.provider)}
                    </span>
                  </div>
                  {status?.connectionStatus === 'active' && (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  {status?.connectionStatus === 'error' && (
                    <XCircle className="w-5 h-5" />
                  )}
                  {status?.connectionStatus === 'testing' && (
                    <AlertTriangle className="w-5 h-5" />
                  )}
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="opacity-75">Status:</span>
                    <span className="font-medium capitalize">
                      {status?.connectionStatus}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-75">Success Rate:</span>
                    <span className="font-medium">
                      {status?.authSuccessRate?.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="opacity-75">Total Attempts:</span>
                    <span className="font-medium">{status?.totalAttempts}</span>
                  </div>
                  {status?.lastTestAt && (
                    <div className="text-xs opacity-75 mt-2">
                      Last tested: {new Date(status?.lastTestAt)?.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Authentication Logs */}
        <div>
          <h3 className="text-sm font-semibold text-foreground mb-4">Recent Authentication Activity</h3>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading logs...</p>
            </div>
          ) : authLogs?.length === 0 ? (
            <div className="text-center py-8 border border-gray-200 rounded-lg">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-muted-foreground">No authentication activity yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {authLogs?.map(log => (
                <div
                  key={log?.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    {log?.success ? (
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate">
                          {log?.email}
                        </span>
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                          {getProviderName(log?.provider)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {log?.success ? 'Successful login' : log?.error_message || 'Authentication failed'}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground flex-shrink-0 ml-4">
                    {new Date(log?.created_at)?.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Troubleshooting Guide */}
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Troubleshooting Tips
          </h4>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Verify certificate is valid and not expired</li>
            <li>Check SSO URL and Entity ID match provider configuration</li>
            <li>Ensure domain is verified before testing authentication</li>
            <li>Confirm attribute mapping matches provider SAML response</li>
            <li>Test connection after any configuration changes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConnectionStatus;